import type { VoxelData, VoxelMap } from '@/voxelTypes'
import { isChunkedVoxelGrid } from '@/storage/grid/ChunkedVoxelGrid'
import { diffGrids, expandCellMutations, type ChunkDelta } from '@/storage/grid/gridDiff'

export type VoxelMutation = {
  key: number
  previous?: VoxelData
  next?: VoxelData
}

export type MutationCandidateCollector = {
  addCandidate: (key: number) => void
}

/**
 * Journal of one reduction's voxel changes (docs/large-volume-plan.md §2.3, §3.2).
 *
 * Two modes:
 * - Chunk mode (`beginChunkDiff()`, the document path: grid bases
 *   on): no collector is installed, `finalize` runs `diffGrids(before, after)` and `mutations`
 *   expands the chunk deltas lazily (and once) when a consumer reads them.
 * - Candidate mode (native `Map` bases): writers record
 *   every visited key through `recordMutationCandidate`, and `finalize` compares each candidate
 *   between the two bases. The document base is always a grid (ADR 0003), so in the app this mode is
 *   reached only through the kill switch; it is kept because reductions over native Maps (previews
 *   composed on sparse layers, tests) still need a journal, and as the fallback when `finalize`
 *   receives bases that are not comparable grids.
 */
export type VoxelMutationJournal = MutationCandidateCollector & {
  readonly candidates: ReadonlySet<number>
  /** Non-null after `finalize` when both bases were grids on one palette (chunk mode). */
  readonly chunkDeltas: readonly ChunkDelta[] | null
  /** Per-cell mutations; in chunk mode a lazy, memoized expansion of `chunkDeltas`. */
  readonly mutations: readonly VoxelMutation[]
  /** True while writers must record candidates (the reducer installs a collector only then). */
  readonly collectsCandidates: boolean
  /** Switches to chunk mode: the caller guarantees `finalize` will diff grid bases. */
  beginChunkDiff: () => void
  finalize: (before: VoxelMap, after: VoxelMap) => void
}

let activeMutationCollector: MutationCandidateCollector | null = null

export function getActiveMutationCandidateCollector(): MutationCandidateCollector | null {
  return activeMutationCollector
}

export function setActiveMutationCandidateCollector(
  collector: MutationCandidateCollector | null,
): MutationCandidateCollector | null {
  const previous = activeMutationCollector
  activeMutationCollector = collector
  return previous
}

export function recordMutationCandidate(key: number): void {
  activeMutationCollector?.addCandidate(key)
}

const EMPTY_MUTATIONS: readonly VoxelMutation[] = Object.freeze([]) as readonly VoxelMutation[]

export function createVoxelMutationJournal(): VoxelMutationJournal {
  const candidates = new Set<number>()
  let chunkMode = false
  let chunkDeltas: readonly ChunkDelta[] | null = null
  let mutations: readonly VoxelMutation[] | null = null
  let expand: (() => readonly VoxelMutation[]) | null = null

  return {
    get candidates() {
      return candidates
    },
    get chunkDeltas() {
      return chunkDeltas
    },
    get mutations() {
      if (mutations === null) {
        mutations = expand ? expand() : EMPTY_MUTATIONS
        expand = null
      }
      return mutations
    },
    get collectsCandidates() {
      return !chunkMode
    },
    beginChunkDiff() {
      chunkMode = true
    },
    addCandidate(key: number) {
      candidates.add(key)
    },
    finalize(before: VoxelMap, after: VoxelMap) {
      mutations = null
      expand = null
      chunkDeltas = null
      if (!chunkMode) {
        mutations = collectVoxelMutations(before, after, candidates)
        return
      }
      if (isChunkedVoxelGrid(before) && isChunkedVoxelGrid(after)) {
        const deltas = diffGrids(before, after)
        if (deltas !== 'incomparable') {
          chunkDeltas = deltas
          if (deltas.length === 0) mutations = EMPTY_MUTATIONS
          else {
            const palette = after.palette
            expand = () => expandCellMutations(deltas, palette)
          }
          return
        }
      }
      // The bases turned out not to be comparable grids (for example a setBase to another
      // representation), so no writer recorded candidates: compare every key of both bases.
      before.forEach((_v, key) => candidates.add(key))
      after.forEach((_v, key) => candidates.add(key))
      mutations = collectVoxelMutations(before, after, candidates)
    },
  }
}

export function collectVoxelMutations(
  before: VoxelMap,
  after: VoxelMap,
  candidates: Iterable<number>,
): VoxelMutation[] {
  const mutations: VoxelMutation[] = []
  for (const key of candidates) {
    const previous = before.get(key)
    const next = after.get(key)
    if (sameVoxelValue(previous, next)) continue
    mutations.push({ key, previous, next })
  }
  return mutations
}

function sameVoxelValue(a: VoxelData | undefined, b: VoxelData | undefined): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.type === b.type &&
    a.color === b.color &&
    a.material === b.material &&
    a.face === b.face &&
    a.topFace === b.topFace
}
